import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PhotoAnnotationEditor — SVG polygon overlay for tagging image regions to bottles.
 *
 * Rendering:
 *   <div style="position:relative">
 *     <img />
 *     <svg viewBox="0 0 1 1" style="position:absolute;inset:0">
 *       polygon per annotation (normalised 0–1 coords)
 *     </svg>
 *   </div>
 *
 * Interactions (when readOnly=false):
 *   - Click  → add vertex to in-progress polygon
 *   - Double-click → close polygon; prompt bottle assignment via dropdown
 *   - Escape → cancel in-progress polygon
 *   - Click existing polygon → select it; show bottle selector + delete button
 *
 * Bottle states:
 *   - Normal in-stock     → blue tint (palette colour per bottle index)
 *   - Selected bottle     → bright blue, thick stroke
 *   - Consumed bottle     → faint red rgba(220,38,38,0.35)
 */
import { useState, useRef, useEffect } from 'react';
import { Trash2, Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';
// ── Colour helpers ─────────────────────────────────────────────────────────────
const PALETTE = [
    '#3b82f6', // blue
    '#8b5cf6', // violet
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#6366f1', // indigo
];
function bottleColour(bottleId, bottles) {
    if (!bottleId)
        return '#6b7280';
    const idx = bottles.findIndex((b) => b.id === bottleId);
    if (idx === -1)
        return '#6b7280';
    return PALETTE[idx % PALETTE.length];
}
function isConsumed(bottle) {
    if (!bottle)
        return false;
    return ['drunk', 'opened', 'gifted', 'sold', 'broken'].includes(bottle.status);
}
// ── SVG helpers ────────────────────────────────────────────────────────────────
function pointsAttr(region) {
    return region.map((p) => `${p.x},${p.y}`).join(' ');
}
// ── Component ─────────────────────────────────────────────────────────────────
export function PhotoAnnotationEditor({ mediaUrl, bottles, annotations, onChange, selectedBottleId, readOnly = false, }) {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
    const [imgLoaded, setImgLoaded] = useState(false);
    // Draw mode state
    const [drawing, setDrawing] = useState([]);
    const [cursor, setCursor] = useState(null); // mouse position while drawing
    // Selection state
    const [selectedAnn, setSelectedAnn] = useState(null); // index into annotations
    const [assignBottleId, setAssignBottleId] = useState('');
    // Capture image natural dimensions after load
    function handleImgLoad() {
        if (imgRef.current) {
            setImgSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
        }
        setImgLoaded(true);
    }
    // Convert pixel event coords → normalised 0–1 coords relative to the rendered image
    function toNorm(e) {
        if (!containerRef.current || !imgRef.current)
            return null;
        const rect = imgRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
    }
    // Keyboard: Escape cancels draw mode
    useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape') {
                setDrawing([]);
                setCursor(null);
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    // ── Mouse interactions ───────────────────────────────────────────────────
    function handleSvgMouseMove(e) {
        if (!drawing.length)
            return;
        setCursor(toNorm(e));
    }
    function handleSvgClick(e) {
        if (readOnly)
            return;
        // If we clicked on a polygon this event is already handled by the polygon handler
        // via stopPropagation; reaching here means we clicked background
        const pt = toNorm(e);
        if (!pt)
            return;
        setSelectedAnn(null);
        setDrawing((prev) => [...prev, pt]);
    }
    function handleSvgDoubleClick(e) {
        if (readOnly)
            return;
        e.preventDefault();
        if (drawing.length < 3) {
            setDrawing([]);
            setCursor(null);
            return;
        }
        // Commit annotation with no bottle assigned yet
        const newAnn = {
            mediaId: '',
            bottleId: null,
            regionData: drawing,
            sortOrder: annotations.length,
        };
        const updated = [...annotations, newAnn];
        onChange?.(updated);
        setSelectedAnn(updated.length - 1);
        setAssignBottleId('');
        setDrawing([]);
        setCursor(null);
    }
    function handlePolygonClick(e, index) {
        if (readOnly)
            return;
        e.stopPropagation();
        // Finish draw mode if active
        if (drawing.length > 0) {
            setDrawing([]);
            setCursor(null);
            return;
        }
        setSelectedAnn(index === selectedAnn ? null : index);
        setAssignBottleId(annotations[index]?.bottleId ?? '');
    }
    function handleAssignBottle() {
        if (selectedAnn === null)
            return;
        const updated = annotations.map((a, i) => i === selectedAnn ? { ...a, bottleId: assignBottleId || null } : a);
        onChange?.(updated);
    }
    function handleDeleteAnnotation() {
        if (selectedAnn === null)
            return;
        const updated = annotations.filter((_, i) => i !== selectedAnn);
        onChange?.(updated);
        setSelectedAnn(null);
    }
    // ── Render ─────────────────────────────────────────────────────────────────
    const inDrawMode = drawing.length > 0;
    return (_jsxs("div", { className: "space-y-3", children: [!readOnly && (_jsx("p", { className: "text-xs text-gray-400", children: inDrawMode
                    ? `${drawing.length} point${drawing.length !== 1 ? 's' : ''} — double-click to close polygon · Esc to cancel`
                    : 'Click on the image to start drawing a region. Click an existing region to select and tag it.' })), _jsxs("div", { ref: containerRef, className: "relative inline-block max-w-full select-none", style: { cursor: !readOnly ? (inDrawMode ? 'crosshair' : 'default') : 'default' }, children: [_jsx("img", { ref: imgRef, src: mediaUrl, alt: "", onLoad: handleImgLoad, className: "block max-w-full rounded-lg", draggable: false }), imgLoaded && (_jsxs("svg", { style: { position: 'absolute', inset: 0, width: '100%', height: '100%' }, viewBox: "0 0 1 1", preserveAspectRatio: "none", onClick: handleSvgClick, onDoubleClick: handleSvgDoubleClick, onMouseMove: handleSvgMouseMove, children: [annotations.map((ann, i) => {
                                const bottle = bottles.find((b) => b.id === ann.bottleId) ?? undefined;
                                const consumed = isConsumed(bottle);
                                const colour = bottleColour(ann.bottleId, bottles);
                                const isSelected = i === selectedAnn || ann.bottleId === selectedBottleId;
                                const fill = consumed ? 'rgba(220,38,38,0.35)' : `${colour}40`; // 25% opacity
                                const stroke = isSelected ? (consumed ? '#dc2626' : colour) : '#6b728060';
                                const strokeWidth = isSelected ? 0.006 : 0.002;
                                return (_jsx("polygon", { points: pointsAttr(ann.regionData), fill: fill, stroke: stroke, strokeWidth: strokeWidth, style: { cursor: readOnly ? 'default' : 'pointer' }, onClick: (e) => handlePolygonClick(e, i) }, i));
                            }), annotations.map((ann, i) => {
                                if (!ann.bottleId)
                                    return null;
                                const bottle = bottles.find((b) => b.id === ann.bottleId);
                                if (!bottle)
                                    return null;
                                const pts = ann.regionData;
                                if (pts.length === 0)
                                    return null;
                                const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
                                const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
                                const bottleIdx = bottles.findIndex((b) => b.id === ann.bottleId);
                                return (_jsx("text", { x: cx, y: cy, textAnchor: "middle", dominantBaseline: "middle", fontSize: "0.028", fill: "white", stroke: "rgba(0,0,0,0.5)", strokeWidth: "0.002", paintOrder: "stroke", style: { pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }, children: bottleIdx + 1 }, `lbl-${i}`));
                            }), drawing.length > 0 && (_jsxs(_Fragment, { children: [_jsx("polyline", { points: [...drawing, ...(cursor ? [cursor] : [])].map((p) => `${p.x},${p.y}`).join(' '), fill: "none", stroke: "#dc2626", strokeWidth: "0.003", strokeDasharray: "0.01 0.005" }), drawing.map((pt, j) => (_jsx("circle", { cx: pt.x, cy: pt.y, r: "0.005", fill: "#dc2626" }, j)))] }))] }))] }), selectedAnn !== null && !readOnly && (_jsxs("div", { className: "rounded-lg border border-gray-200 p-3 space-y-2 bg-gray-50", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("p", { className: "text-sm font-medium text-gray-700", children: "Tag this region" }), _jsx("button", { type: "button", onClick: handleDeleteAnnotation, className: "text-gray-400 hover:text-red-500", title: "Delete region", children: _jsx(Trash2, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("select", { value: assignBottleId, onChange: (e) => setAssignBottleId(e.target.value), className: "input-field text-sm flex-1", children: [_jsx("option", { value: "", children: "No bottle tagged" }), bottles.map((b, idx) => (_jsxs("option", { value: b.id, children: ["#", idx + 1, b.packagingLabel ? ` · ${b.packagingLabel}` : '', isConsumed(b) ? ' (consumed)' : ''] }, b.id)))] }), _jsx("button", { type: "button", onClick: handleAssignBottle, className: "btn-primary btn-sm", title: "Assign", children: _jsx(Check, { className: "h-3.5 w-3.5" }) }), _jsx("button", { type: "button", onClick: () => setSelectedAnn(null), className: "btn-secondary btn-sm", children: _jsx(X, { className: "h-3.5 w-3.5" }) })] }), annotations[selectedAnn]?.bottleId && (() => {
                        const bottle = bottles.find((b) => b.id === annotations[selectedAnn].bottleId);
                        return bottle ? (_jsxs("p", { className: cn('text-xs', isConsumed(bottle) ? 'text-red-500' : 'text-gray-500'), children: [bottle.wineName, bottle.vintage ? ` ${bottle.vintage}` : '', isConsumed(bottle) ? ' — consumed' : ''] })) : null;
                    })()] })), annotations.some((a) => a.bottleId) && (_jsx("div", { className: "flex flex-wrap gap-2", children: bottles.map((b, idx) => {
                    const tagged = annotations.some((a) => a.bottleId === b.id);
                    if (!tagged)
                        return null;
                    const colour = PALETTE[idx % PALETTE.length];
                    const consumed = isConsumed(b);
                    return (_jsxs("span", { className: cn('inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium', consumed ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-700'), children: [_jsx("span", { className: "inline-block h-2 w-2 rounded-full flex-shrink-0", style: { backgroundColor: consumed ? '#dc2626' : colour } }), "#", idx + 1, b.packagingLabel ? ` · ${b.packagingLabel}` : '', consumed ? ' (consumed)' : ''] }, b.id));
                }) }))] }));
}
